import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GitBranch, ExternalLink } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import ListPageHeader from '@components/common/ListPageHeader';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import { RZTable, DateCell } from '@components/common/table';
import ErrorState from '@components/common/ErrorState';
import { conflictService } from '@services';
import type { Conflict, ConflictContractOwner } from '@app-types/conflict';
import type { TableColumn } from '@app-types';
import { formatMediumDate } from '@utils/dateUtils';
import styles from './DivisionConflictWorklistPage.module.css';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const conflictColumns: TableColumn[] = [
  { key: 'contract', label: 'Contract', defaultVisible: true },
  { key: 'supplier', label: 'Supplier', defaultVisible: true },
  { key: 'owners', label: 'Contract Owners', defaultVisible: true },
  { key: 'detected', label: 'Detected', defaultVisible: true },
  { key: 'action', label: '', defaultVisible: true, alwaysVisible: true }
];

// A division conflict is raised either on a single contract or on a meeting that bundles
// several contracts; for a meeting, entity_id is a meeting id, not a contract id.
const isMeetingConflict = (conflict: Conflict): boolean => conflict.entity_type === 'meeting';

// Owners come from entity_info (live data). conflict.context only holds the snapshot taken
// when the conflict was detected, so it goes stale once owners change.
const getOwners = (conflict: Conflict): ConflictContractOwner[] => {
  if (!isMeetingConflict(conflict)) return conflict.entity_info?.contract_owners ?? [];

  const uniqueOwners = new Map<number, ConflictContractOwner>();
  (conflict.entity_info?.contracts ?? []).forEach((contract) => {
    contract.owners.forEach((owner) => uniqueOwners.set(owner.user_id, owner));
  });
  return Array.from(uniqueOwners.values());
};

const getSupplierNames = (conflict: Conflict): string[] => {
  if (!isMeetingConflict(conflict)) {
    return conflict.entity_info?.supplier_name ? [conflict.entity_info.supplier_name] : [];
  }
  const names = (conflict.entity_info?.contracts ?? [])
    .map((contract) => contract.supplier_name)
    .filter((name): name is string => Boolean(name));
  return Array.from(new Set(names));
};

// Meeting-level conflicts are resolved from the Scheduler, where the bundle lives.
const getConflictRoute = (conflict: Conflict): string => {
  if (!isMeetingConflict(conflict)) return `/contracts/${conflict.entity_id}`;

  const params = new URLSearchParams({ myMeetings: 'false', status: 'conflicts' });
  const firstContractNumber = conflict.entity_info?.contracts?.[0]?.contract_number;
  if (firstContractNumber) params.set('search', firstContractNumber);
  return `/scheduler?${params.toString()}`;
};

const DivisionConflictWorklistPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['conflicts-worklist', 'DIVISION_CONFLICT', currentPage, pageSize],
    queryFn: () =>
      conflictService.getWorklist({
        conflictTypeCode: 'DIVISION_CONFLICT',
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      })
  });

  const conflicts = data?.conflicts ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const renderContractCell = (conflict: Conflict) => {
    if (!isMeetingConflict(conflict)) {
      return (
        <div className={styles.contractCell}>
          <div>{conflict.entity_info?.contract_number ?? '-'}</div>
          <div className={styles.contractName}>{conflict.entity_info?.contract_name ?? ''}</div>
        </div>
      );
    }

    const contracts = conflict.entity_info?.contracts ?? [];
    if (contracts.length === 0) return '-';
    return (
      <div className={styles.bundleCell}>
        <Badge variant="default">Meeting bundle</Badge>
        {contracts.map((contract) => (
          <div key={contract.id} className={styles.contractCell}>
            <div>{contract.contract_number}</div>
            <div className={styles.contractName}>{contract.name}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderCell = (column: TableColumn, conflict: Conflict) => {
    switch (column.key) {
      case 'contract':
        return renderContractCell(conflict);

      case 'supplier': {
        const supplierNames = getSupplierNames(conflict);
        if (supplierNames.length === 0) return '-';
        return (
          <div className={styles.contractCell}>
            {supplierNames.map((name) => (
              <div key={name}>{name}</div>
            ))}
          </div>
        );
      }

      case 'owners': {
        const owners = getOwners(conflict);
        if (owners.length === 0) return '-';
        return (
          <div className={styles.ownersCell}>
            {owners.map((owner) => (
              <div key={owner.user_id} className={styles.ownerRow}>
                <span>{owner.user_name}</span>
                <Badge variant="default">{owner.division_name ?? 'No Division'}</Badge>
              </div>
            ))}
          </div>
        );
      }

      case 'detected':
        return <DateCell date={conflict.detected_at} formatDate={(d) => formatMediumDate(d, 'Unknown')} />;

      case 'action':
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(getConflictRoute(conflict));
            }}
            title={isMeetingConflict(conflict) ? 'Open in Scheduler' : 'Open Contract'}
          >
            <ExternalLink size={16} />
          </Button>
        );

      default:
        return '-';
    }
  };

  if (error) {
    return (
      <PageLayout title="Division Conflict Resolution Worklist">
        <ErrorState message="Error loading division conflicts. Please try again." />
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Division Conflict Resolution Worklist">
      <ListPageHeader icon={<GitBranch size={24} />} title="Division Conflicts" count={total} />

      <RZTable
        data={conflicts}
        columns={conflictColumns}
        getRowKey={(conflict) => conflict.id}
        renderCell={renderCell}
        onRowClick={(conflict) => navigate(getConflictRoute(conflict))}
        pagination={{
          currentPage,
          totalPages,
          totalItems: total,
          pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
          itemName: 'conflicts',
          pageSizeOptions: PAGE_SIZE_OPTIONS
        }}
        state={{
          isLoading,
          isFetching,
          isEmpty: conflicts.length === 0,
          emptyTitle: 'No division conflicts',
          emptyDescription: 'All contracts have consistent divisions across their contract owners.',
          loadingMessage: 'Loading division conflicts...'
        }}
      />
    </PageLayout>
  );
};

export default DivisionConflictWorklistPage;
